import { useState, useEffect, useMemo } from "react";
import useFetch from "./useFetch";
import { useTranslation } from "react-i18next";

const useGetArea = (cityID) => {
  const { t } = useTranslation();

  const [data, setData] = useState({ areas: [] });
  const [dataCityID, setDataCityID] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tries, setTries] = useState(0);

  const retry = () => setTries((prev) => prev + 1);

  const Params = new URLSearchParams({
    populate: "*",
    locale: "en",
    "pagination[limit]": -1,
    "filters[City][id][$eq]": cityID,
  });
  if (tries > 0) Params.set("_retry", tries);

  const areasUrl = `/areas?${Params.toString()}`;
  
  const { data: areasData, loading: areasLoading, error: areasError } = useFetch(cityID ? areasUrl : "");
  const processedAreas = useMemo(() => {
    if (!areasData || !Array.isArray(areasData)) return [];

    const list = [...areasData];
    list.push({
      id: 0,
      attributes: {
        Name: t("checkout.all-areas"),
        City: { data: { id: cityID } },
      },
    });

    return list;
  }, [areasData, cityID, t]);

  useEffect(() => {
    if (!cityID) {
      setData({ areas: [] });
      setDataCityID(null);
      setLoading(false);
      return;
    }

    if (areasError) {
      // Still attribute the data to the current city, otherwise the staleness
      // check keeps `loading` stuck on true and the area list never settles.
      setData({ areas: [] });
      setDataCityID(cityID);
      setError(true);
      setLoading(false);
      return;
    }

    if (!areasLoading) {
      setData({ areas: processedAreas });
      setDataCityID(cityID);
      setLoading(false);
      setError(false);
    } else {
      // A new city is being fetched. Keep reporting loading and drop the
      // previous city's areas so nothing downstream can read stale data.
      setLoading(true);
      setData({ areas: [] });
      setDataCityID(null);
    }
  }, [areasLoading, processedAreas, areasError, cityID]);

  // Treat the data as unusable until it belongs to the currently selected
  // city. This closes the one-frame window between the id changing and the
  // next request reporting itself as in-flight.
  // Truthiness (not `!= null`) so an unset id of `0` or `""` matches the
  // `!cityID` early return above and does not pin `loading` to true forever.
  const isStale = !!cityID && dataCityID != cityID;

  return {
    data: isStale ? { areas: [] } : data,
    loading: isStale || loading,
    error,
    setError,
    retry,
  };
};

export { useGetArea };